import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LoginModal from '../components/LoginModal';
import SignupModal from '../components/SignupModal';
import Navbar from '../../../shared/components/Navbar';
import { getApiBaseUrl } from '../../../core/config/api';
import type { User } from '../../../shared/types';

const HomeDesktop: React.FC = () => {
  const [showLogin, setShowLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const check = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), 3000);
        const r = await fetch(`${getApiBaseUrl(true)}/admin/maintenance/status`, {
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        if (r.ok) {
          const data = await r.json();
          if (data.success && data.data) {
            setMaintenanceEnabled(data.data.enabled === true || data.data.enabled === 1);
          }
        }
      } catch {
        /* ignore */
      }
    };
    void check();
    const id = window.setInterval(check, 30000);
    return () => clearInterval(id);
  }, []);

  const handleLoginSuccess = (user: User) => {
    setShowLogin(false);
    if (maintenanceEnabled && user.roles_id === 1) {
      navigate('/panel');
      return;
    }
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-600 via-red-700 to-red-800 flex flex-col">
      <Navbar onLoginClick={() => setShowLogin(true)} />
      <div className="flex flex-1 items-center justify-center px-4 py-8 pt-20">
      <div className="max-w-md w-full mx-4">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img
              src="/images/logo.png"
              alt="OpSecours"
              className="w-24 h-24"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">OpSecours</h1>
          <p className="text-red-100 text-lg">Application</p>
          {maintenanceEnabled && (
            <p className="mt-4 text-sm text-amber-100 bg-black/20 rounded-lg px-3 py-2">
              Maintenance active — connexion réservée aux <strong>CM</strong>.
            </p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-2xl p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Bienvenue</h2>

          <p className="text-gray-600 mb-6 text-center">
            {maintenanceEnabled
              ? 'Connectez-vous avec un compte CM pour accéder au panel.'
              : 'Connectez-vous pour accéder à votre compte OpSecours'}
          </p>

          <div className="space-y-4">
            <button
              type="button"
              onClick={() => setShowLogin(true)}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
            >
              Se connecter
            </button>

            {!maintenanceEnabled && (
              <button
                type="button"
                onClick={() => setShowSignup(true)}
                className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
              >
                Créer un compte
              </button>
            )}
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500 text-center">Version • OpSecours</p>
          </div>
        </div>

        {showLogin && (
          <LoginModal
            isOpen={showLogin}
            onClose={() => setShowLogin(false)}
            onSwitchToSignup={() => {
              setShowLogin(false);
              setShowSignup(true);
            }}
            onSuccess={handleLoginSuccess}
            maintenanceCmOnly={maintenanceEnabled}
          />
        )}

        {showSignup && !maintenanceEnabled && (
          <SignupModal
            isOpen={showSignup}
            onClose={() => setShowSignup(false)}
            onSwitchToLogin={() => {
              setShowSignup(false);
              setShowLogin(true);
            }}
            onSuccess={handleLoginSuccess}
          />
        )}
      </div>
      </div>
    </div>
  );
};

export default HomeDesktop;
